'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2ComparisonChoice, PrV2VersionComparison } from './lib/pr-v2-comparison-choices';
import { useNow } from '@/hooks/use-now';
import { getFromNow } from '@/lib/utils/date-formatters';
type PrV2SinceLastReviewNoticeProps = {
  /** The picker's shortcuts; "Since your last review" names the range, "All changes" undoes it. */
  comparisonChoices: PrV2ComparisonChoice[];
  fileCount: number;
  /** Changed files in the whole PR, when the summary is cached. */
  totalFileCount: number | null | undefined;
  lastReviewedSubmittedAt: string | null;
  setComparison: (comparison: PrV2VersionComparison) => void;
};

/**
 * Sits above the first file while the diff is automatically narrowed to what changed since the
 * viewer's last review, so the narrower file list doesn't read as the whole PR.
 */
export function PrV2SinceLastReviewNotice(t0) {
  const $ = _c(18);
  const {
    comparisonChoices,
    fileCount,
    totalFileCount,
    lastReviewedSubmittedAt,
    setComparison
  } = t0;
  const now = useNow();
  const rangeLabel = comparisonChoices.find(_temp)?.rangeLabel;
  let t1;
  if ($[0] !== comparisonChoices) {
    t1 = comparisonChoices.find(_temp2);
    $[0] = comparisonChoices;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const allChanges = t1;
  const shownTotal = typeof totalFileCount === "number" && totalFileCount > fileCount ? totalFileCount : null;
  const fileLabel = (shownTotal ?? fileCount) === 1 ? "file" : "files";
  const t2 = shownTotal === null ? `${fileCount} ${fileLabel}` : `${fileCount} of ${shownTotal} ${fileLabel}`;
  let t3;
  if ($[2] !== lastReviewedSubmittedAt || $[3] !== now) {
    t3 = lastReviewedSubmittedAt ? getFromNow(lastReviewedSubmittedAt, {
      now
    }) : null;
    $[2] = lastReviewedSubmittedAt;
    $[3] = now;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== rangeLabel || $[6] !== t2 || $[7] !== t3) {
    t4 = [rangeLabel, t2, t3].filter(Boolean);
    $[5] = rangeLabel;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const details = t4;
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="text-zinc-200">Changes since your last review</span>;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = ` · ${details.join(" \xB7 ")}`;
  let t7;
  if ($[10] !== t6) {
    t7 = <span className="min-w-0 truncate">{t5}<span className="text-zinc-400">{t6}</span></span>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== allChanges || $[13] !== setComparison) {
    t8 = allChanges ? <button type="button" className="ml-auto shrink-0 rounded border border-zinc-800 px-2.5 py-1 text-xs text-zinc-200 hover:bg-zinc-800" onClick={() => setComparison(allChanges.comparison)}>Show all changes</button> : null;
    $[12] = allChanges;
    $[13] = setComparison;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7 || $[16] !== t8) {
    t9 = <div className="flex min-w-0 items-center gap-3 rounded-md border border-zinc-800 bg-zinc-900/70 px-3 py-2 text-sm" data-testid="pr-v2-since-last-review-notice">{t7}{t8}</div>;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
}
function _temp2(choice_0) {
  return choice_0.id === "all-changes";
}
function _temp(choice) {
  return choice.id === "last-review";
}